import { validatePhone } from 'utils/phone_validators';
import { useTranslation } from 'react-i18next';
import { Checkbox, Select, TextField } from 'Components/ui';
import styled from 'styled-components';

// ─── Styled components ─────────────────────────────────────────────────────────

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin: 2px 0 0;
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CheckboxLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
  cursor: pointer;
`;


// ─── Component ────────────────────────────────────────────────────────────────

export const Detalhes = ({
  handleComplete,
  register,
  handleSubmit,
  errors,
  phoneFormat,
  countryCode,
  allConfigs = [],
  isCountryConfigError,
  setPhoneCountry,
  watch,
  setValue,
  users = [],
}) => {
  const { t } = useTranslation(['company']);

  const onSubmit = () => handleComplete();

  return (
    <form id="office-form" onSubmit={handleSubmit(onSubmit)}>
      <FieldGrid>

        {/* Name */}
        <FieldGroup data-tour="office-name">
          <FieldLabel>{t('company:offices.form.name')}</FieldLabel>
          <TextField
            fullWidth
            error={!!errors?.name}
            autoFocus
            placeholder={t('company:offices.form.namePlaceholder')}
            {...register('name', { required: t('company:offices.form.nameRequired') })}
          />
          {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
        </FieldGroup>

        {/* Address */}
        <FieldGroup data-tour="office-address">
          <FieldLabel>{t('company:offices.form.address')}</FieldLabel>
          <TextField
            fullWidth
            placeholder={t('company:offices.form.addressPlaceholder')}
            {...register('address')}
          />
        </FieldGroup>

        {/* Phone */}
        <FieldGroup data-tour="office-phone">
          <FieldLabel>{t('company:offices.form.phone')}</FieldLabel>
          <PhoneRow>
            {allConfigs.length > 0 && (
              <Select
                name="phoneCountry"
                value={countryCode || ''}
                onChange={value => setPhoneCountry(value)}
                options={allConfigs.map(c => ({ value: c.countryCode, label: c.phonePrefix }))}
                fullWidth={false}
                sx={{ width: 90, flexShrink: 0 }}
                aria-label={t('company:offices.form.country')}
              />
            )}
            <TextField
              error={!!errors?.phone}
              placeholder={t('company:offices.form.phonePlaceholder')}
              sx={{ flex: 1 }}
              {...register('phone', {
                validate: value => {
                  if (!value) return true;
                  if (!validatePhone(value, phoneFormat)) {
                    return t('company:offices.form.phoneInvalid', { format: phoneFormat });
                  }
                  return true;
                },
              })}
            />
          </PhoneRow>
          {errors?.phone && <ErrorMsg>{errors.phone.message}</ErrorMsg>}
        </FieldGroup>

        {/* Responsible — a real reference to an existing tenant user, not free text */}
        <FieldGroup data-tour="office-responsible">
          <FieldLabel>{t('company:offices.form.responsible')}</FieldLabel>
          <Select
            name="responsible"
            value={watch('responsible') || ''}
            onChange={value => setValue('responsible', value)}
            options={[
              { value: '', label: t('company:offices.form.responsiblePlaceholder') },
              ...users.map(u => ({ value: u._id, label: u.name })),
            ]}
          />
        </FieldGroup>

        {/* City */}
        <FieldGroup data-tour="office-city">
          <FieldLabel>{t('company:offices.form.city')}</FieldLabel>
          <TextField
            fullWidth
            placeholder={t('company:offices.form.cityPlaceholder')}
            {...register('city')}
          />
        </FieldGroup>

        {/* State */}
        <FieldGroup data-tour="office-state">
          <FieldLabel>{t('company:offices.form.state')}</FieldLabel>
          <TextField
            fullWidth
            placeholder={t('company:offices.form.statePlaceholder')}
            {...register('state')}
          />
        </FieldGroup>

        {/* Country — same country-config list used across the app (Company Profile, Clientes) */}
        <FieldGroup data-tour="office-country">
          <FieldLabel>{t('company:offices.form.addressCountry')}</FieldLabel>
          <Select
            name="country"
            value={watch('country') || ''}
            onChange={value => setValue('country', value)}
            disabled={isCountryConfigError}
            error={isCountryConfigError}
            helperText={isCountryConfigError ? t('company:offices.form.addressCountryError') : ''}
            options={[
              {
                value: '',
                label: isCountryConfigError
                  ? t('company:offices.form.addressCountryError')
                  : t('company:offices.form.addressCountryPlaceholder'),
              },
              ...allConfigs.map(cc => ({ value: cc.countryCode, label: cc.name.pt })),
            ]}
          />
        </FieldGroup>

      </FieldGrid>

      <CheckboxRow>
        <Checkbox
          id="office-is-headquarters"
          checked={!!watch('isHeadquarters')}
          onChange={e => setValue('isHeadquarters', e.target.checked)}
        />
        <CheckboxLabel htmlFor="office-is-headquarters">
          {t('company:offices.form.isHeadquarters')}
        </CheckboxLabel>
      </CheckboxRow>

      <CheckboxRow>
        <Checkbox
          id="office-active"
          checked={!!watch('active')}
          onChange={e => setValue('active', e.target.checked)}
        />
        <CheckboxLabel htmlFor="office-active">
          {t('company:offices.form.active')}
        </CheckboxLabel>
      </CheckboxRow>
    </form>
  );
};
